import { useMemo, useState } from 'react';
import { Search, ScrollText } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Badge } from '@/ui/base/badge';
import { Input } from '@/ui/base/input';
import { EmptyState } from '@/ui/EmptyState';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';
import { formatarDinheiro } from '@/nucleo/dinheiro';

import type { CadinResumo } from './tipos';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dados: CadinResumo;
}

/** Lista somente-leitura de devedores CADIN — drill-down do indicador "Pendências CADIN". */
export function CadinDevedoresModal({ open, onOpenChange, dados }: Props) {
  const [busca, setBusca] = useState('');

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return dados.devedores;
    return dados.devedores.filter(
      (d) =>
        d.credenciado.toLowerCase().includes(termo) ||
        d.documento.toLowerCase().includes(termo) ||
        d.processo.toLowerCase().includes(termo),
    );
  }, [dados.devedores, busca]);

  const total = useMemo(
    () => dados.devedores.reduce((acc, d) => acc + d.valorPendente, 0),
    [dados.devedores],
  );

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Pendências CADIN"
      description="Credenciados ativos com débito municipal em aberto."
      desktopMaxWidth="max-w-4xl"
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge variant="outline" className="gap-1">
            <ScrollText className="h-3 w-3" aria-hidden />
            {dados.devedores.length} devedor(es)
          </Badge>
          <Badge variant="secondary" className="font-mono">
            {formatarDinheiro(total)}
          </Badge>
          <Badge variant="outline">Fonte: simulada</Badge>
          {dados.ultimaConsulta && (
            <span className="ml-auto text-xs text-muted-foreground">
              Última consulta:{' '}
              {format(new Date(dados.ultimaConsulta), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
            </span>
          )}
        </div>

        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome, documento ou processo…"
            aria-label="Buscar devedor CADIN"
            className="h-9 pl-8"
          />
        </div>

        {filtrados.length === 0 ? (
          <EmptyState
            title={dados.devedores.length === 0 ? 'Nenhuma pendência CADIN' : 'Sem resultados'}
            description={
              dados.devedores.length === 0
                ? 'Use o botão "Atualizar CADIN" no painel para sincronizar com a fonte.'
                : 'Ajuste a busca para ampliar o resultado.'
            }
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Credenciado</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Processo CADIN</TableHead>
                  <TableHead>Motivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtrados.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">{d.credenciado}</TableCell>
                    <TableCell className="font-mono text-xs">{d.documento}</TableCell>
                    <TableCell className="text-right font-mono">
                      {formatarDinheiro(d.valorPendente)}
                    </TableCell>
                    <TableCell className="text-xs">{d.processo}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{d.motivo}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
}
